import React from "react";
import { Img, staticFile } from "remotion";
import { FrakLogo } from "../../components/FrakLogo";
import { theme } from "../../brand";
import type { Merchant } from "../merchants";

/**
 * The merchant logo as an app-icon tile. Logos arrive either as an opaque
 * square with their own background (Comblée, emy) or as a transparent /
 * white mark; the tile is filled with the logo's own measured background
 * so both read as the same object. Opaque logos (already a tile, with
 * their own margins) fill it edge to edge; transparent marks are padded
 * (~1/10) so no wordmark ever touches the rounded edge.
 */
export const LogoTile: React.FC<{
  m: Merchant;
  size: number;
  style?: React.CSSProperties;
  /** Frak badge on the corner ("recommend it with Frak"). */
  badge?: boolean;
}> = ({ m, size, style, badge = false }) => {
  const radius = size * 0.24;
  const b = size * 0.42;
  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        flex: "none",
        ...style,
      }}
    >
      <div
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          background: m.colors.tile,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow:
            "0 24px 60px -18px rgba(0,0,0,0.45), 0 0 0 2px rgba(255,255,255,0.35)",
        }}
      >
        <Img
          src={staticFile(m.logo)}
          style={
            m.logoOpaque
              ? { width: "100%", height: "100%", objectFit: "cover" }
              : { width: "80%", height: "80%", objectFit: "contain" }
          }
        />
      </div>
      {badge ? (
        <div
          style={{
            position: "absolute",
            right: -b * 0.28,
            bottom: -b * 0.28,
            width: b,
            height: b,
            borderRadius: b * 0.28,
            background: theme.brand.colors.primary[600],
            border: `${Math.max(3, b * 0.06)}px solid ${theme.brand.colors.neutral.white}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 24px -8px rgba(0,0,0,0.4)",
          }}
        >
          <FrakLogo
            variant="icon"
            width={b * 0.56}
            color={theme.brand.colors.neutral.white}
          />
        </div>
      ) : null}
    </div>
  );
};

/** "avec [frak]" / "with [frak]": the quiet co-brand line. */
export const WithFrak: React.FC<{
  label: string;
  color: string;
  size?: number;
  white?: boolean;
}> = ({ label, color, size = 30, white = false }) => (
  <div style={{ display: "flex", alignItems: "center", gap: size * 0.4 }}>
    <span
      style={{
        fontFamily: theme.fontFamily.inter,
        fontWeight: theme.fontWeight.medium,
        fontSize: size,
        color,
      }}
    >
      {label}
    </span>
    <FrakLogo
      width={size * 3.1}
      style={white ? { filter: "brightness(0) invert(1)" } : undefined}
    />
  </div>
);
